// Everything is shown in Irish time, whatever the device's own time zone.
export const TIMEZONE = 'Europe/Dublin';

const timeFmt = new Intl.DateTimeFormat('en-IE', { timeZone: TIMEZONE, hour: '2-digit', minute: '2-digit', hour12: false });
const dayFmt = new Intl.DateTimeFormat('en-IE', { timeZone: TIMEZONE, weekday: 'short', day: 'numeric', month: 'short' });
const isoDayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' });

export const time = (iso: string | number) => timeFmt.format(new Date(iso));
export const dayLabel = (iso: string | number) => dayFmt.format(new Date(iso));

// YYYY-MM-DD of the Irish day containing the moment.
export const irishDate = (when: Date | number = Date.now()) => isoDayFmt.format(new Date(when));

// The moment an Irish day starts (midnight Europe/Dublin), in ms. Clocks change at 01:00, so
// the offset at midnight UTC is the offset at Irish midnight too.
export function irishMidnight(date: string): number {
  const utc = Date.parse(`${date}T00:00:00Z`);
  const wall = new Date(utc).toLocaleString('sv-SE', { timeZone: TIMEZONE }).replace(' ', 'T');
  return utc - (Date.parse(`${wall}Z`) - utc);
}

export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function duration(minutes: number): string {
  if (minutes < 1) return 'under 1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h < 24) return m ? `${h} h ${m} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} d ${h % 24} h`;
}

export function ago(iso: string | null, now = Date.now()): string {
  if (!iso) return '';
  const min = (now - new Date(iso).getTime()) / 60_000;
  if (min < 1) return 'just now';
  return `${duration(min)} ago`;
}

export const km = (v: number | null | undefined) => (v === null || v === undefined ? '–' : `${v.toLocaleString('en-IE', { maximumFractionDigits: 1 })} km`);
export const kmh = (v: number | null | undefined) => (v === null || v === undefined ? '–' : `${Math.round(v)} km/h`);

export const STATUS_LABEL = {
  driving: 'Driving',
  idling: 'Idling',
  stopped: 'Stopped',
  offline: 'Offline',
  'no-data': 'No data yet',
} as const;

const dateTimeFmt = new Intl.DateTimeFormat('en-IE', {
  timeZone: TIMEZONE, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});
const hourFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TIMEZONE, hour: '2-digit', hourCycle: 'h23' });

// "14 Oct 2026, 10:22:31" in Irish time.
export const dateTime = (iso: string | number) => dateTimeFmt.format(new Date(iso));
// Hour of the day (0–23) in Irish time.
export const irishHour = (iso: string | number) => Number(hourFmt.format(new Date(iso)));

export const TYPE_LABEL = {
  truck: 'Truck', van: 'Van', car: 'Car', trailer: 'Trailer', tractor: 'Tractor', plant: 'Plant', other: 'Other',
} as const;

export const UNIT_STATUS_LABEL = {
  ok: 'OK', sleep: 'Sleep', nogps: 'No GPS', nodata: 'No data', nopower: 'No power',
} as const;

export const UNIT_STATUS_HELP = {
  ok: 'Reporting normally',
  sleep: 'Parked and reporting less often to save power',
  nogps: 'Last record had no GPS fix',
  nodata: 'Nothing received for over 3 hours',
  nopower: 'External power is off or disconnected',
} as const;


// Fuel: litres used, and economy in litres per 100 km (as on Irish and UK fleet reports).
export const litresText = (l: number) => `${l.toLocaleString('en-IE', { maximumFractionDigits: 0 })} L`;
export const economy = (lPer100: number) => `${lPer100.toLocaleString('en-IE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} L/100 km`;

// Subscriptions of self-install trackers.
export const SUB_LABEL = { active: 'Active', expiring: 'Expiring soon', grace: 'Ended – grace period', expired: 'No active subscription' } as const;
const dayOnlyFmt = new Intl.DateTimeFormat('en-IE', { timeZone: 'Europe/Dublin', day: 'numeric', month: 'short', year: 'numeric' });
export const dayOnly = (iso: string | number) => dayOnlyFmt.format(new Date(iso));
export const daysFrom = (iso: string, now = Date.now()) => Math.round((new Date(iso).getTime() - now) / 86_400_000);
